import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { config } from '../config/config';

const isEditableTarget = (target) => {
  if (!target?.tagName) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  return Boolean(target.isContentEditable);
};

/**
 * Scan on dom with keydown. Waiting keypress and execute methods with/without redux dispatch
 */
export const useBarcodeScannerWaiting = (
  requeiredLength,
  isEnableScan,
  arrayConditions,
  ...methodToExecWithReceivedData
) => {
  const [scannedData, setScannedData] = useState(null);
  const dispatch = useDispatch();

  arrayConditions.unshift(isEnableScan);
  useEffect(() => {
    let code = null;
    let timeStart = null;
    let timeoutSet = false;
    let inputTime = null;
    let lastScanTime = 0;
    const SCAN_PAUSE = config.scanPause ?? 2000;
    const ENTER_GUARD_MS = 600;

    const blurActiveElement = () => {
      const el = document.activeElement;
      if (el && el !== document.body && el !== document.documentElement) {
        el.blur();
      }
    };

    const handleScannedCode = () => {
      const currentTime = Date.now();
      if (currentTime - lastScanTime > SCAN_PAUSE) {
        lastScanTime = currentTime;
        code = code.substr(0, requeiredLength);
        console.log('useScanWaiting->debug->handleScannedCode->code', code);
        setScannedData(code);
        if (isEnableScan) {
          methodToExecWithReceivedData.forEach((mthd) => {
            if (mthd.dispatch) {
              dispatch(mthd.mthd(code));
            } else {
              mthd.mthd(code);
            }
          });
        } else {
          console.log('useScanWaiting->debug->DISABLED');
        }
        blurActiveElement();
      } else {
        console.log('useScanWaiting->debug->Skipped scan due to short pause');
      }
      console.log('useScanWaiting->debug->clearCode');
      code = null;
    };

    const scanWaiting = (event) => {
      if (isEditableTarget(event.target)) return;

      // Scanner suffix Enter must not activate focused toolbar buttons (Start/Pause).
      if (event.key === 'Enter') {
        const sinceLastScan = Date.now() - lastScanTime;
        if (code && code.length >= 8) {
          event.preventDefault();
          event.stopPropagation();
          handleScannedCode();
          return;
        }
        if (sinceLastScan < ENTER_GUARD_MS) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }

      if (event.key?.length !== 1) return;

      event.preventDefault();
      event.stopPropagation();

      inputTime = timeStart ? Date.now() - timeStart : 1;
      if (!code || inputTime > config.scanCodeMax) {
        timeStart = Date.now();
        code = event.key;
        timeoutSet = false;
      } else {
        code += event.key;
      }
      if (code.length >= 8 && !timeoutSet) {
        timeoutSet = true;
        setTimeout(() => {
          if (
            code &&
            code.length >= 8 &&
            inputTime <= config.scanCodeMax + 10
          ) {
            handleScannedCode();
          } else {
            console.log('useScanWaiting->debug->clearCode');
            code = null;
          }
          timeStart = null;
          inputTime = null;
        }, config.scanCodeMax + 200);
      }
      try {
        code = code.trim().replace(/^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g, '');
        code = code.replace(/[\r\n]+/g, '');
        code = code.replace('~', '');
        console.log('useScanWaiting->debug->ScannedCode', code);
      } catch (error) {
        /* ignore */
      }
      if (
        code.length >= requeiredLength &&
        inputTime < config.scanCodeMax &&
        inputTime > config.scanCodeMin
      ) {
        inputTime = null;
        handleScannedCode();
      } else if (inputTime > config.scanCodeMax) {
        console.log('useScanWaiting->debug->clearCode');
        code = null;
        inputTime = null;
        timeoutSet = true;
      }
    };

    if (isEnableScan) {
      document.addEventListener('keydown', scanWaiting, true);
    }

    return () => {
      document.removeEventListener('keydown', scanWaiting, true);
    };
  }, arrayConditions); // eslint-disable-line react-hooks/exhaustive-deps

  return scannedData;
};
